/* eslint-disable react/prop-types */
import CategoryServiceSelect from 'categoryServices/components/CategoryServiceSelect';
import Button from 'common/components/Button/Button';
import PageHeader from 'common/components/PageHeader/PageHeader';
import Table from 'common/components/Table/Table';
import TranslatedText from 'common/components/TranslatedText/TranslatedText';
import useRouter from 'common/hooks/use-router';
import useTrans from 'common/hooks/use-trans';
import formatPrice from 'common/utils/formatPrice';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useService from 'services/contexts/services';
import SiteSelect from 'sites/components/SiteSelect';
import useSite from 'sites/contexts/sites';

const ServicesList = () => {
  const { t } = useTranslation();
  const { translateText } = useTrans();
  const router = useRouter();
  const { currentSite } = useSite();
  const {
    fetchItems: fetchServices, items: services, isFetching, pageCount, remove, update, filters, setFilter,
    itemsCount,
  } = useService();

  const handleDelete = useCallback(async (row) => {
    await remove(row.original.id);
    await fetchServices();
  }, [remove, fetchServices]);

  const handleEdit = useCallback((row) => router.push(`/services/edit/${row.original.id}`), [router]);

  const handleToggle = useCallback(async (id) => {
    const service = services && services.find((p) => p.id === id);

    await update(service.id, { ...service, active: !service.active });
    await fetchServices();
  }, [update, fetchServices, services]);

  const columns = useMemo(() => [
    {
      Header: t('common.name'),
      filterName: 'name_contains',
      Cell: ({ row: { original: { id, name } } }) => (
        <span className="table-link" onClick={() => router.push(`/services/edit/${id}`)}>
          <TranslatedText value={name} />
        </span>
      ),
    },
    {
      Header: t('common.reference'),
      accessor: 'reference',
      filterName: 'reference_contains',
    },
    {
      Header: t('common.price'),
      Cell: ({ row: { original: { option, price, variant_prices: variantPrices } } }) => {
        if (option && variantPrices.length) {
          return variantPrices.filter(({ price }) => price !== null).map(({ price, option_variant: variant }) => (
            <div>
              {`${translateText(variant.name)}: ${formatPrice(price, currentSite?.currency)}`}
            </div>
          ));
        }
        return (
          <span>
            {`${formatPrice(price, currentSite?.currency)}`}
          </span>
        );
      },
    },
    {
      Header: t('services.category'),
      filterName: 'category_service.id',
      filterComponent: (props) => <CategoryServiceSelect isFilter {...props} />,
      Cell: ({ row: { original: { category_service: category } } }) => (<TranslatedText value={category?.name} />),
    },
    {
      Header: t('common.site'),
      filterName: 'site.id',
      hidden: !!currentSite,
      filterComponent: (props) => <SiteSelect isFilter {...props} />,
      Cell: ({ row: { original: { site } } }) => (
        <span>
          {site?.name}
        </span>
      ),
    },
    {
      Header: t('common.isOnSale'),
      accessor: 'active',
      Cell: ({ row: { original: { active, id } } }) => (
        <Button
          onClick={() => handleToggle(id)}
          icon={active ? 'fa-check' : 'fa-times'}
          color={active ? 'success' : 'danger'}
          transparent
        />
      ),
    },
    {
      Header: t('common.actions'),
      className: 'table-actions',
      Cell: ({ row }) => (
        <>
          <Button
            onClick={() => handleEdit(row)}
            icon="fa-pencil-alt"
            color="primary"
            transparent
          />
          <Button
            onClick={() => handleDelete(row)}
            icon="fa-trash-alt"
            color="danger"
            confirm
            transparent
            confirmMessage={t('services.confirmDelete')}
          />
        </>
      ),
    },
  ], [t, currentSite, translateText, handleToggle, handleEdit, handleDelete, router]);

  return (
    <>
      <PageHeader
        title={t('menu.listServices')}
        link={{ url: '/services/add', icon: 'pizza-slice', label: t('services.addService') }}
      />
      <section className="section list-page has-overflow-x-table">
        <Table
          data={services}
          columns={columns}
          fetchData={fetchServices}
          isFetching={isFetching}
          pageCount={pageCount}
          filters={filters}
          setFilter={setFilter}
          itemsCount={itemsCount}
        />
      </section>
    </>
  );
};

export default ServicesList;
